/* eslint-disable @stylistic/max-len */
import { AxiosPromise } from 'axios';

import {
  BillsBillCreatePayload,
  BillsBillPaperworkTypeResponse,
  BillsBillResponse,
  BillsBillsResponse,
  BillsBillUpdatePayload,
  BillsBillValidationRulesResponse,
  BillsGetBillStatusesResponse,
  BillsPresentResponse,
  BillsUpdateBillByPayeePayload,
  SettlementGetBillsParams,
} from './types';
import { baseClientConstructorProps } from '../config';
import { BaseClient, BaseClientConstructorProps, QueryParams, SendRequestPayload } from '../generated/baseClient';

class SettlementBillsClient extends BaseClient {
  private _namespace: string;

  constructor(props: BaseClientConstructorProps) {
    super(props);

    this._namespace = '/api/v1/settlement/bills';
  }

  readonly createBill = (payload: BillsBillCreatePayload): AxiosPromise<BillsBillResponse> => {
    const sendRequestPayload: SendRequestPayload = {
      data: payload,
      endpoint: this._namespace,
      method: 'POST',
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this._sendRequest(sendRequestPayload);
  };

  readonly deleteBill = (id: string): AxiosPromise<BillsBillResponse> => {
    const sendRequestPayload: SendRequestPayload = {
      endpoint: `${this._namespace}/${id}`,
      method: 'DELETE',
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this._sendRequest(sendRequestPayload);
  };

  readonly getBillPaperworkType = (id: string): AxiosPromise<BillsBillPaperworkTypeResponse> => {
    const sendRequestPayload: SendRequestPayload = {
      endpoint: `${this._namespace}/${id}/paperwork_type`,
      method: 'GET',
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this._sendRequest(sendRequestPayload);
  };

  readonly getBill = (id: string): AxiosPromise<BillsBillResponse> => {
    const sendRequestPayload: SendRequestPayload = {
      endpoint: `${this._namespace}/${id}`,
      method: 'GET',
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this._sendRequest(sendRequestPayload);
  };

  readonly getBillStatuses = (): AxiosPromise<BillsGetBillStatusesResponse> => {
    const sendRequestPayload: SendRequestPayload = {
      endpoint: `${this._namespace}/statuses`,
      method: 'GET',
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this._sendRequest(sendRequestPayload);
  };

  readonly getBillsPresent = (): AxiosPromise<BillsPresentResponse> => {
    const sendRequestPayload: SendRequestPayload = {
      endpoint: `${this._namespace}/present`,
      method: 'GET',
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this._sendRequest(sendRequestPayload);
  };

  readonly getBills = (queryParams: SettlementGetBillsParams): AxiosPromise<BillsBillsResponse> => {
    const sendRequestPayload: SendRequestPayload = {
      endpoint: this._namespace,
      method: 'GET',
      queryParams: queryParams as unknown as QueryParams,
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this._sendRequest(sendRequestPayload);
  };

  readonly getValidationRules = (id: string): AxiosPromise<BillsBillValidationRulesResponse> => {
    const sendRequestPayload: SendRequestPayload = {
      endpoint: `${this._namespace}/${id}/validation_rules`,
      method: 'GET',
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this._sendRequest(sendRequestPayload);
  };

  readonly resubmitBills = (ids: string[]): AxiosPromise<{ status: 'Success' }> => {
    const sendRequestPayload: SendRequestPayload = {
      data: { billIds: ids },
      endpoint: `${this._namespace}/resubmit_to_sqs`,
      method: 'POST',
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this._sendRequest(sendRequestPayload);
  };

  readonly updateBill = (id: string, payload: BillsBillUpdatePayload): AxiosPromise<BillsBillResponse> => {
    const sendRequestPayload: SendRequestPayload = {
      data: payload,
      endpoint: `${this._namespace}/${id}`,
      method: 'PUT',
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this._sendRequest(sendRequestPayload);
  };

  readonly updateBillByPayee = (id: string, payload: BillsUpdateBillByPayeePayload): AxiosPromise<BillsBillResponse> => {
    const sendRequestPayload: SendRequestPayload = {
      data: payload,
      endpoint: `${this._namespace}/${id}`,
      method: 'PUT',
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this._sendRequest(sendRequestPayload);
  };
}

export const settlementBillsClient = new SettlementBillsClient(baseClientConstructorProps);
